import { z } from "zod";
import { callSummaryContentSchema } from "./call";
import { TEMPLATE_VARIABLE_SOURCES, followupCadenceSchema } from "./followup";
import { SITYWEB_SITE_CATEGORIES } from "./site-categories";
import { aiSocialProfileSchema } from "./social";

export const WORKSPACE_ROLES = ["owner", "admin", "operator", "reviewer"] as const;
export const workspaceRoleSchema = z.enum(WORKSPACE_ROLES);
export type WorkspaceRole = z.infer<typeof workspaceRoleSchema>;

export const MEMBER_STATUSES = ["invited", "active", "suspended"] as const;
export const memberStatusSchema = z.enum(MEMBER_STATUSES);
export type MemberStatus = z.infer<typeof memberStatusSchema>;

export const LEAD_SOURCES = [
  "manual",
  "whatsapp",
  "meta_lead_ads",
  "import",
  "instagram",
  "messenger",
  "sityweb",
  "google_ads",
] as const;
export const leadSourceSchema = z.enum(LEAD_SOURCES);
export type LeadSource = z.infer<typeof leadSourceSchema>;

export const LEAD_STAGES = [
  "new",
  "contacted",
  "qualified",
  "proposal",
  "won",
  "lost",
  "unqualified",
  "archived",
] as const;
export const leadStageSchema = z.enum(LEAD_STAGES);
export type LeadStage = z.infer<typeof leadStageSchema>;

/** `meta_messaging`: a Facebook Page and its Instagram account (ADR 0092). */
export const CONNECTION_KINDS = ["whatsapp", "meta_lead_ads", "meta_messaging"] as const;
export const connectionKindSchema = z.enum(CONNECTION_KINDS);
export type ConnectionKind = z.infer<typeof connectionKindSchema>;

export const CONNECTION_STATUSES = ["pending", "active", "expired", "revoked", "error"] as const;
export const connectionStatusSchema = z.enum(CONNECTION_STATUSES);
export type ConnectionStatus = z.infer<typeof connectionStatusSchema>;

export const CONVERSATION_CHANNELS = ["whatsapp", "instagram", "messenger", "internal"] as const;
export const conversationChannelSchema = z.enum(CONVERSATION_CHANNELS);
export type ConversationChannel = z.infer<typeof conversationChannelSchema>;
export function isConversationChannel(value: unknown): value is ConversationChannel {
  return conversationChannelSchema.safeParse(value).success;
}

/** The channels a contact writes from, one chat each (ADR 0092). */
export const CHAT_CHANNELS = ["whatsapp", "instagram", "messenger"] as const;
export const chatChannelSchema = z.enum(CHAT_CHANNELS);
export type ChatChannel = z.infer<typeof chatChannelSchema>;
export function isChatChannel(value: unknown): value is ChatChannel {
  return chatChannelSchema.safeParse(value).success;
}

export const MESSAGE_DIRECTIONS = ["inbound", "outbound", "internal"] as const;
export const messageDirectionSchema = z.enum(MESSAGE_DIRECTIONS);
export type MessageDirection = z.infer<typeof messageDirectionSchema>;

export const MESSAGE_ORIGINS = [
  "contact",
  "member",
  "api",
  "whatsapp_business_app",
  "system",
  /** Sent from Meta Business Suite or the Instagram app (ADR 0092). */
  "business_app",
] as const;
export const messageOriginSchema = z.enum(MESSAGE_ORIGINS);
export type MessageOrigin = z.infer<typeof messageOriginSchema>;

export const MESSAGE_TYPES = [
  "text",
  "image",
  "audio",
  "video",
  "document",
  "location",
  "contacts",
  "sticker",
  "interactive",
  "reaction",
  "template",
  "system",
] as const;
export const messageTypeSchema = z.enum(MESSAGE_TYPES);
export type MessageType = z.infer<typeof messageTypeSchema>;

export const MESSAGE_STATUSES = [
  "queued",
  "sent",
  "delivered",
  "read",
  "failed",
  "received",
] as const;
export const messageStatusSchema = z.enum(MESSAGE_STATUSES);
export type MessageStatus = z.infer<typeof messageStatusSchema>;

export const PROPOSAL_KINDS = [
  "whatsapp_message",
  "connector_action",
  "lead_update",
  "followup_change",
  "appointment",
] as const;
export const proposalKindSchema = z.enum(PROPOSAL_KINDS);
export type ProposalKind = z.infer<typeof proposalKindSchema>;

export const PROPOSAL_STATUSES = [
  "pending_approval",
  "approved",
  "scheduled",
  "running",
  "completed",
  "rejected",
  "cancelled",
  "failed",
  "superseded",
  "expired",
] as const;
export const proposalStatusSchema = z.enum(PROPOSAL_STATUSES);
export type ProposalStatus = z.infer<typeof proposalStatusSchema>;

export const CONNECTOR_KINDS = ["sityweb_api", "generic_webhook"] as const;
export const connectorKindSchema = z.enum(CONNECTOR_KINDS);
export type ConnectorKind = z.infer<typeof connectorKindSchema>;

export const CONNECTOR_CAPABILITIES = ["create_draft", "edit_site"] as const;
export const connectorCapabilitySchema = z.enum(CONNECTOR_CAPABILITIES);
export type ConnectorCapability = z.infer<typeof connectorCapabilitySchema>;

export const TEMPLATE_STATUSES = [
  "draft",
  "pending",
  "approved",
  "rejected",
  "paused",
  "disabled",
] as const;
export const templateStatusSchema = z.enum(TEMPLATE_STATUSES);
export type TemplateStatus = z.infer<typeof templateStatusSchema>;

export const AUDIT_ACTOR_KINDS = ["user", "system", "provider", "ai"] as const;
export const auditActorKindSchema = z.enum(AUDIT_ACTOR_KINDS);
export type AuditActorKind = z.infer<typeof auditActorKindSchema>;

export const emailSchema = z.string().trim().toLowerCase().email().max(320);
export const uuidSchema = z.string().uuid();
export const e164Schema = z
  .string()
  .regex(/^\+[1-9]\d{6,14}$/, "Inserisci il numero in formato internazionale");
export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9][a-z0-9-]{1,46}[a-z0-9]$/);

export const consentSchema = z
  .object({
    status: z.enum(["unknown", "granted", "withdrawn"]),
    source: z.string().trim().max(200).nullable(),
    recordedAt: z.string().datetime({ offset: true }).nullable(),
  })
  .strict();
export type Consent = z.infer<typeof consentSchema>;

export const leadFieldsSchema = z.record(
  z.string().trim().min(1).max(80),
  z.union([z.string().max(4_000), z.number(), z.boolean(), z.null()]),
);

const manualLeadObjectSchema = z
  .object({
    displayName: z.string().trim().min(1).max(200),
    firstName: z.string().trim().max(100).nullable().optional(),
    lastName: z.string().trim().max(100).nullable().optional(),
    phone: e164Schema.nullable().optional(),
    email: emailSchema.nullable().optional(),
    companyName: z.string().trim().max(200).nullable().optional(),
    stage: leadStageSchema.default("new"),
    assigneeMemberId: uuidSchema.nullable().optional(),
    fields: leadFieldsSchema.default({}),
    tags: z.array(z.string().trim().min(1).max(60)).max(50).default([]),
    consent: consentSchema.optional(),
  })
  .strict();

export const manualLeadInputSchema = manualLeadObjectSchema.refine(
  (value) => value.phone || value.email,
  {
    message: "Inserisci almeno un numero di telefono o un indirizzo email",
    path: ["phone"],
  },
);
export type ManualLeadInput = z.infer<typeof manualLeadInputSchema>;

export const leadPatchSchema = manualLeadObjectSchema
  .omit({ displayName: true })
  .partial()
  .extend({
    displayName: z.string().trim().min(1).max(200).optional(),
    version: z.number().int().positive(),
  })
  .strict();
export type LeadPatch = z.infer<typeof leadPatchSchema>;

export const mergeLeadInputSchema = z
  .object({
    targetLeadId: uuidSchema,
    sourceVersion: z.number().int().positive(),
    targetVersion: z.number().int().positive(),
    /**
     * "Unisci con un altro lead…": the operator chose the pair, also without the
     * same number or email (owner, 2026-10-21). Without it, only a pair Polaris suggested.
     */
    manual: z.boolean().optional(),
  })
  .strict();
export type MergeLeadInput = z.infer<typeof mergeLeadInputSchema>;

/**
 * Which of the lead's chats a message goes to, the tab open in the chat: one of
 * its chats, or WhatsApp for a lead with a number and no WhatsApp chat yet.
 * Without either, the lead's chat (`leadChatOrder`).
 */
export const chatChoiceShape = {
  conversationId: uuidSchema.optional(),
  channel: chatChannelSchema.optional(),
};
export type ChatChoice = { conversationId?: string; channel?: ChatChannel };

export const manualMessageInputSchema = z
  .object({
    content: z.string().trim().min(1).max(4_096),
    templateId: uuidSchema.nullable().optional(),
    templateParameters: z.array(z.string().trim().min(1).max(1_024)).max(20).default([]),
    scheduledFor: z.string().datetime({ offset: true }).nullable().optional(),
    /** "Rispondi": the message of the chat this one quotes (ADR 0048). */
    replyToMessageId: uuidSchema.nullable().optional(),
    ...chatChoiceShape,
    idempotencyKey: z.string().trim().min(8).max(200),
  })
  .strict();
export type ManualMessageInput = z.infer<typeof manualMessageInputSchema>;

/** A reaction to a message of the chat; an empty emoji removes ours (ADR 0048). */
export const messageReactionInputSchema = z
  .object({
    emoji: z
      .string()
      .max(16)
      .refine((value) => !/[\s\p{L}\p{N}]/u.test(value), "Usa una sola emoji."),
    idempotencyKey: z.string().trim().min(8).max(200),
  })
  .strict();
export type MessageReactionInput = z.infer<typeof messageReactionInputSchema>;

/** Text copied from WhatsApp for a message Meta did not hand over; empty removes it (ADR 0052). */
export const messageTextInputSchema = z.object({ text: z.string().trim().max(4_096) }).strict();
export type MessageTextInput = z.infer<typeof messageTextInputSchema>;

export const whatsappProposalPayloadSchema = z
  .object({
    kind: z.literal("whatsapp_message"),
    content: z.string().trim().min(1).max(4_096),
    templateId: uuidSchema.nullable(),
  })
  .strict();

// Contratto portabile della capability `create_draft`. Il ricevitore esterno
// decide slug, ownership e lifecycle della risorsa: l'AI fornisce soltanto il
// brief approvabile dall'operatore, mai identificatori autorevoli.
export const createDraftInputSchema = z
  .object({
    businessName: z.string().trim().min(1).max(120),
    siteBrief: z.string().trim().min(1).max(10_000),
    // Sityweb's category for the business, picked by the AI and approved by
    // the operator. Absent: Sityweb infers it before generating.
    category: z
      .enum(SITYWEB_SITE_CATEGORIES)
      .nullable()
      .optional()
      .transform((value) => value ?? undefined),
    contactName: z
      .string()
      .trim()
      .min(1)
      .max(120)
      .nullable()
      .optional()
      .transform((value) => value ?? undefined),
    location: z
      .string()
      .trim()
      .min(1)
      .max(200)
      .nullable()
      .optional()
      .transform((value) => value ?? undefined),
  })
  .strict();
export type CreateDraftInput = z.infer<typeof createDraftInputSchema>;

/**
 * The Polaris member who approved a `create-draft`, beside `input` (which is
 * the AI's brief): Sityweb shows it as the author of the site.
 */
export const sitywebDraftRequesterSchema = z
  .object({
    name: z.string().trim().min(1).max(200).nullable(),
    email: z.string().trim().toLowerCase().email().max(320),
  })
  .strict();
export type SitywebDraftRequester = z.infer<typeof sitywebDraftRequesterSchema>;

/**
 * Technical cap on the lead's files of a site action: an edit request puts one
 * line per file in the editor prompt, which holds about 190 of them.
 */
export const MAX_SITE_ACTION_FILES = 150;

/**
 * Instructions of an edit request: the cap of a message in Sityweb's admin
 * chat (96 KiB). Sityweb refuses, with its message, a request that does not fit
 * the editor prompt together with the file lines.
 */
export const SITE_EDIT_INSTRUCTIONS_MAX_LENGTH = 96 * 1024;

/**
 * How Sityweb treats the logo file: `ready` as it is (a PDF becomes an image),
 * `extract` drawn from a photo or a document, as "Ricavalo da una foto" in the
 * new site form (ADR 0035 §7).
 */
export const LOGO_MODES = ["ready", "extract"] as const;
export const logoModeSchema = z.enum(LOGO_MODES);
export type LogoMode = z.infer<typeof logoModeSchema>;

// The draft proposal also carries the lead's files (ADR 0035): Polaris uploads
// them to the new site after `create-draft`, which never receives their ids.
export const createDraftProposalInputSchema = createDraftInputSchema
  .extend({
    /** Polaris attachment ids of the lead's messages. */
    attachmentIds: z.array(uuidSchema).max(MAX_SITE_ACTION_FILES).default([]),
    /** One of `attachmentIds`, uploaded as the site logo. */
    logoAttachmentId: uuidSchema
      .nullable()
      .optional()
      .transform((value) => value ?? undefined),
    /** With a logo: use it as it is or extract it; absent = as it is. */
    logoMode: logoModeSchema
      .nullable()
      .optional()
      .transform((value) => value ?? undefined),
  })
  .strict();
export type CreateDraftProposalInput = z.infer<typeof createDraftProposalInputSchema>;

/** What `create-draft` receives: the proposal without the files. */
export function createDraftRequestInput(input: CreateDraftProposalInput): CreateDraftInput {
  return createDraftInputSchema.parse({
    businessName: input.businessName,
    siteBrief: input.siteBrief,
    category: input.category ?? null,
    contactName: input.contactName ?? null,
    location: input.location ?? null,
  });
}

export const connectorResultSchema = z
  .object({
    externalResourceType: z.string().min(1).max(100),
    externalResourceId: z.string().min(1).max(500),
    url: z
      .string()
      .url()
      .regex(/^https:\/\//i, "L'URL della risorsa esterna deve usare HTTPS.")
      .nullable()
      .optional(),
    summary: z.string().max(1_000).nullable().optional(),
  })
  .strict();
export type ConnectorResult = z.infer<typeof connectorResultSchema>;

// Capability `edit_site` (ADR 0027): instructions for the site editor and the
// files the lead sent, uploaded to the site before the request.
export const editSiteInputSchema = z
  .object({
    /** Sityweb site id; absent = the site linked to the lead. */
    siteId: z
      .string()
      .trim()
      .min(1)
      .max(100)
      .nullable()
      .optional()
      .transform((value) => value ?? undefined),
    instructions: z.string().trim().min(1).max(SITE_EDIT_INSTRUCTIONS_MAX_LENGTH),
    publish: z.boolean().optional(),
    /** Polaris attachment ids of the lead's messages. */
    attachmentIds: z.array(uuidSchema).max(MAX_SITE_ACTION_FILES).default([]),
  })
  .strict();
export type EditSiteInput = z.infer<typeof editSiteInputSchema>;

export const connectorProposalPayloadSchema = z
  .object({
    kind: z.literal("connector_action"),
    connectorId: uuidSchema,
    capability: connectorCapabilitySchema,
    input: z.union([createDraftProposalInputSchema, editSiteInputSchema]),
  })
  .strict();

export const leadUpdateProposalPayloadSchema = z
  .object({
    kind: z.literal("lead_update"),
    stage: z.preprocess(
      (value) => (value === null ? undefined : value),
      leadStageSchema.optional(),
    ),
    tags: z.preprocess(
      (value) => (value === null ? undefined : value),
      z.array(z.string().trim().min(1).max(60)).max(50).optional(),
    ),
    fields: z.preprocess(
      (value) => (value === null ? undefined : value),
      leadFieldsSchema.optional(),
    ),
  })
  .strict();

/** A day of the workspace calendar, `YYYY-MM-DD`. */
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Indica una data valida.");

/**
 * "Sposta" or "Chiudi" on a follow-up (ADR 0055): prepared by the AI on the
 * day of the follow-up, applied once an operator confirms.
 */
export const followupChangeProposalPayloadSchema = z
  .object({
    kind: z.literal("followup_change"),
    followupId: uuidSchema,
    operation: z.enum(["reschedule", "close"]),
    /** reschedule: the day to write again, in the workspace zone. */
    date: isoDateSchema.nullable(),
    /** close: "lost" when the lead said no; null when nothing is pending. */
    stage: z.enum(["lost"]).nullable(),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();
export type FollowupChangeProposalPayload = z.infer<typeof followupChangeProposalPayloadSchema>;

export const APPOINTMENT_MODES = ["call", "video", "in_person", "other"] as const;
export const appointmentModeSchema = z.enum(APPOINTMENT_MODES);
export type AppointmentMode = z.infer<typeof appointmentModeSchema>;

export const APPOINTMENT_STATUSES = ["scheduled", "done", "missed", "cancelled"] as const;
export const appointmentStatusSchema = z.enum(APPOINTMENT_STATUSES);
export type AppointmentStatus = z.infer<typeof appointmentStatusSchema>;

export type AppointmentOrigin = "member" | "ai";

/** Wall-clock time in the workspace zone, `YYYY-MM-DDTHH:mm`, as the AI reads the current time. */
export const localDateTimeSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, "Indica giorno e ora.");

export const APPOINTMENT_MAX_MINUTES = 720;
export const appointmentDurationSchema = z.number().int().min(5).max(APPOINTMENT_MAX_MINUTES);
/** Minutes before the start for the reminder; null = none. */
export const appointmentReminderSchema = z.number().int().min(0).max(1_440).nullable();

/**
 * An appointment agreed in the chat (ADR 0059): the AI only records a day and
 * time the lead accepted, or the lead's request to move or cancel one.
 */
export const appointmentProposalPayloadSchema = z
  .object({
    kind: z.literal("appointment"),
    operation: z.enum(["create", "reschedule", "cancel"]),
    /** reschedule, cancel: one of the lead's scheduled appointments. */
    appointmentId: uuidSchema.nullable(),
    /** create, reschedule: the start, in the workspace zone. */
    startsAt: localDateTimeSchema.nullable(),
    durationMinutes: appointmentDurationSchema.nullable(),
    mode: appointmentModeSchema.nullable(),
    title: z.string().trim().min(1).max(200).nullable(),
    /** Address, link or number the lead gave. */
    location: z.string().trim().max(500).nullable(),
    reason: z.string().trim().min(1).max(500),
    /** Calendar chosen on the card; null = the lead's assignee, else whoever approves. */
    memberId: uuidSchema.nullable().default(null),
  })
  .strict();
export type AppointmentProposalPayload = z.infer<typeof appointmentProposalPayloadSchema>;

export const proposalPayloadSchema = z
  .discriminatedUnion("kind", [
    whatsappProposalPayloadSchema,
    connectorProposalPayloadSchema,
    leadUpdateProposalPayloadSchema,
    followupChangeProposalPayloadSchema,
    appointmentProposalPayloadSchema,
  ])
  .superRefine((payload, context) => {
    if (payload.kind === "followup_change" && payload.operation === "reschedule" && !payload.date) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["date"],
        message: "Indica il giorno in cui riscrivere.",
      });
    }
    if (payload.kind === "appointment") {
      const missing = (path: string, message: string) =>
        context.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
      if (payload.operation === "create") {
        if (payload.appointmentId) missing("appointmentId", "Un appuntamento nuovo non ha id.");
        if (!payload.startsAt) missing("startsAt", "Indica giorno e ora dell'appuntamento.");
        if (!payload.durationMinutes) missing("durationMinutes", "Indica la durata.");
        if (!payload.mode)
          missing("mode", "Indica se è una chiamata, una videochiamata o un incontro.");
        if (!payload.title) missing("title", "Dai un titolo all'appuntamento.");
      } else {
        if (!payload.appointmentId) missing("appointmentId", "Indica l'appuntamento.");
        if (payload.operation === "reschedule" && !payload.startsAt) {
          missing("startsAt", "Indica il nuovo giorno e la nuova ora.");
        }
      }
      return;
    }
    // The input must match the capability, not just any connector input.
    if (payload.kind !== "connector_action") return;
    const schema =
      payload.capability === "edit_site" ? editSiteInputSchema : createDraftProposalInputSchema;
    if (!schema.safeParse(payload.input).success) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["input"],
        message: "Parametri non validi per questa azione.",
      });
      return;
    }
    if (
      "logoAttachmentId" in payload.input &&
      payload.input.logoAttachmentId &&
      !payload.input.attachmentIds.includes(payload.input.logoAttachmentId)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["input", "logoAttachmentId"],
        message: "Il logo deve essere uno dei file scelti.",
      });
    }
  });
export type ProposalPayload = z.infer<typeof proposalPayloadSchema>;

export const aiProposalOutputSchema = z
  .object({
    summary: z.string().trim().min(1).max(1_000),
    rationale: z.string().trim().min(1).max(1_000),
    proposals: z.array(proposalPayloadSchema).min(1).max(5),
    /** Profiles the AI judged to be the lead's, only when it could search them (ADR 0034). */
    socialProfiles: z.array(aiSocialProfileSchema).max(4).optional(),
    /** The call the job follows, at a glance (ADR 0067); null without one. */
    callSummary: callSummaryContentSchema.nullable().optional(),
    /**
     * The lead's words when they no longer want the site action waiting for approval
     * (ADR 0085); null otherwise. The operator decides.
     */
    siteActionWithdrawn: z
      .string()
      .trim()
      .max(500)
      .nullable()
      .optional()
      .transform((value) => value || null),
    /** A commercial assessment for the operator to confirm; never an automatic decision. */
    conversionSuggestion: z
      .object({
        kind: z.enum(["qualified", "opportunity", "preferred"]),
        reason: z.string().trim().min(1).max(500),
      })
      .strict()
      .nullable()
      .optional(),
  })
  .strict();
export type AiProposalOutput = z.infer<typeof aiProposalOutputSchema>;

/**
 * The lead no longer wants the site action waiting for approval (ADR 0085): their
 * words and when the AI read them. The proposal stays; the operator decides.
 */
export const leadWithdrewSchema = z.object({
  words: z.string().min(1).max(500),
  at: z.string().datetime({ offset: true }),
});
export type LeadWithdrew = z.infer<typeof leadWithdrewSchema>;

/**
 * What the AI decides on the day of a follow-up for a lead who wrote at least
 * once (ADR 0055): write to them, move the follow-up to the day they gave, or
 * close it. Every field is present for OpenAI Structured Outputs.
 */
export const followupPreparationOutputSchema = z
  .object({
    decision: z.enum(["send", "reschedule", "close"]),
    text: z.string().trim().min(1).max(4_096).nullable(),
    date: isoDateSchema.nullable(),
    stage: z.enum(["lost"]).nullable(),
    reason: z.string().trim().min(1).max(500),
  })
  .strict()
  .superRefine((output, context) => {
    if (output.decision === "send" && !output.text) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["text"], message: "Manca il testo." });
    }
    if (output.decision === "reschedule" && !output.date) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["date"],
        message: "Manca il giorno.",
      });
    }
  });
export type FollowupPreparationOutput = z.infer<typeof followupPreparationOutputSchema>;

/**
 * What the operator says about an AI proposal (ADR 0051). "Vale per tutti i
 * lead" is on by default: it is turned off only for lead-specific corrections.
 */
export const AI_FEEDBACK_KINDS = ["revision", "edit", "rejection", "note"] as const;
export const aiFeedbackKindSchema = z.enum(AI_FEEDBACK_KINDS);
export type AiFeedbackKind = z.infer<typeof aiFeedbackKindSchema>;

export const AI_FEEDBACK_NOTE_MAX = 2_000;

export const proposalFeedbackSchema = z
  .object({
    note: z.string().trim().max(AI_FEEDBACK_NOTE_MAX).nullable().optional(),
    general: z.boolean().default(true),
  })
  .strict();
export type ProposalFeedback = z.infer<typeof proposalFeedbackSchema>;

export const proposalPatchSchema = z
  .object({
    payload: proposalPayloadSchema,
    scheduledFor: z.string().datetime({ offset: true }).nullable().optional(),
    version: z.number().int().positive(),
    /** Why the operator edited the AI text by hand. */
    feedback: proposalFeedbackSchema.optional(),
  })
  .strict();
export type ProposalPatch = z.infer<typeof proposalPatchSchema>;

export const proposalRejectInputSchema = z
  .object({
    version: z.number().int().positive(),
    note: z.string().trim().max(AI_FEEDBACK_NOTE_MAX).nullable().optional(),
    general: z.boolean().default(true),
  })
  .strict();
export type ProposalRejectInput = z.input<typeof proposalRejectInputSchema>;

/** "Correggi con AI": the AI rewrites the proposal following the operator's request. */
export const proposalReviseInputSchema = z
  .object({
    version: z.number().int().positive(),
    instructions: z.string().trim().min(1).max(AI_FEEDBACK_NOTE_MAX),
    general: z.boolean().default(true),
  })
  .strict();
export type ProposalReviseInput = z.input<typeof proposalReviseInputSchema>;

/** A note for the AI: on a proposal, on a lead, or a general rule from the settings. */
export const aiFeedbackNoteInputSchema = z
  .object({
    leadId: uuidSchema.nullable().optional(),
    proposalId: uuidSchema.nullable().optional(),
    note: z.string().trim().min(1).max(AI_FEEDBACK_NOTE_MAX),
    general: z.boolean().default(true),
  })
  .strict();
export type AiFeedbackNoteInput = z.input<typeof aiFeedbackNoteInputSchema>;

export const inviteInputSchema = z
  .object({
    email: emailSchema,
    role: workspaceRoleSchema.exclude(["owner"]),
  })
  .strict();
export type InviteInput = z.infer<typeof inviteInputSchema>;

export const acceptInviteInputSchema = z
  .object({
    token: z.string().min(32).max(512),
    name: z.string().trim().min(2).max(160).optional(),
    password: z.string().min(12).max(200).optional(),
  })
  .strict();
export type AcceptInviteInput = z.infer<typeof acceptInviteInputSchema>;

export const templateButtonSchema = z
  .object({
    type: z.enum(["QUICK_REPLY", "URL", "PHONE_NUMBER"]),
    text: z.string().trim().min(1).max(25),
    value: z.string().trim().max(2_048).nullable(),
  })
  .strict();

/** How many positional variables (`{{1}}`, `{{2}}`, …) a template body has. */
export function templateBodyVariableCount(body: string): number {
  return new Set([...body.matchAll(/\{\{(\d+)\}\}/g)].map((match) => match[1])).size;
}

const templateFieldsSchema = z
  .object({
    name: z
      .string()
      .trim()
      .regex(/^[a-z0-9_]{1,512}$/),
    language: z
      .string()
      .trim()
      .regex(/^[a-z]{2}(?:_[A-Z]{2})?$/),
    category: z.enum(["MARKETING", "UTILITY", "AUTHENTICATION"]),
    body: z
      .string()
      .trim()
      .min(1)
      .max(4_096)
      .refine((body) => {
        const indexes = [...body.matchAll(/\{\{(\d+)\}\}/g)]
          .map((match) => Number(match[1]))
          .filter(Number.isFinite);
        const unique = [...new Set(indexes)].sort((left, right) => left - right);
        return unique.every((value, index) => value === index + 1);
      }, "Le variabili del corpo devono essere consecutive: {{1}}, {{2}}, …")
      .refine(
        (body) => !/^\{\{\d+\}\}|\{\{\d+\}\}$/.test(body),
        "Il testo non può iniziare o finire con una variabile: Meta lo rifiuta.",
      ),
    /** Sample value of each `{{n}}`, in order: Meta reviews a template only with them. */
    examples: z
      .array(
        z
          .string()
          .trim()
          .min(1, "Scrivi un esempio per ogni variabile.")
          .max(200)
          .refine((value) => !/[\n\t]| {4,}/.test(value), "Un esempio va su una riga sola."),
      )
      .max(10)
      .default([]),
    /**
     * Follow-up templates only: the lead data each `{{n}}` is filled with, in
     * order, so that the follow-up card can fill it (ADR 0061).
     */
    sources: z.array(z.enum(TEMPLATE_VARIABLE_SOURCES).nullable()).max(10).default([]),
    header: z
      .string()
      .trim()
      .max(1_024)
      .refine((value) => !value.includes("{{"), "Inserisci le variabili nel corpo del template.")
      .nullable()
      .optional(),
    footer: z.string().trim().max(60).nullable().optional(),
    buttons: z.array(templateButtonSchema).max(10).default([]),
  })
  .strict();

function checkTemplateExamples(
  input: { body: string; examples: string[] },
  context: z.RefinementCtx,
): void {
  const count = templateBodyVariableCount(input.body);
  if (input.examples.length === count) return;
  context.addIssue({
    code: z.ZodIssueCode.custom,
    path: ["examples"],
    message:
      count === 0
        ? "Il testo non ha variabili: togli gli esempi."
        : "Scrivi un esempio per ogni variabile: Meta lo chiede per approvare il template.",
  });
}

/** A follow-up template fills every variable from the lead: none can be left to the operator. */
export function followupSourcesComplete(body: string, sources: readonly unknown[]): boolean {
  const count = templateBodyVariableCount(body);
  return sources.length === count && sources.every((source) => source !== null);
}

export const FOLLOWUP_SOURCES_MESSAGE =
  "Nei template di follow-up ogni variabile si riempie con un dato del lead: scegli quale.";

/** Where a template is offered: the follow-up cards, or the chat composer only. */
export const TEMPLATE_USES = ["followup", "other"] as const;
export type TemplateUse = (typeof TEMPLATE_USES)[number];

const templateInputFieldsSchema = templateFieldsSchema.extend({
  use: z.enum(TEMPLATE_USES).default("other"),
});

function checkTemplateInput(
  input: { body: string; examples: string[]; sources: unknown[]; use: TemplateUse },
  context: z.RefinementCtx,
): void {
  checkTemplateExamples(input, context);
  if (input.use === "followup" && !followupSourcesComplete(input.body, input.sources)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["sources"],
      message: FOLLOWUP_SOURCES_MESSAGE,
    });
  }
}

export const templateInputSchema = templateInputFieldsSchema.superRefine(checkTemplateInput);
export type TemplateInput = z.infer<typeof templateInputSchema>;

/** "Nuovo template": the fields and the WhatsApp connection it belongs to. */
export const templateCreateInputSchema = templateInputFieldsSchema
  .extend({ connectionId: uuidSchema })
  .superRefine(checkTemplateInput);

/** "Sposta nei follow-up" / "Sposta in Altri" (ADR 0061): nothing changes on Meta. */
export const templateUseInputSchema = z
  .object({
    use: z.enum(TEMPLATE_USES),
    sources: z.array(z.enum(TEMPLATE_VARIABLE_SOURCES).nullable()).max(10).default([]),
  })
  .strict();
export type TemplateUseInput = z.input<typeof templateUseInputSchema>;

/** "Modifica" (ADR 0061): name and language identify the template on Meta and stay. */
export const templateUpdateInputSchema = templateFieldsSchema
  .omit({ name: true, language: true })
  .superRefine(checkTemplateExamples);
export type TemplateUpdateInput = z.infer<typeof templateUpdateInputSchema>;

/**
 * What "Modifica" changes (ADR 0061): a template Meta never received is edited
 * in Polaris; one Meta approved, rejected or paused is edited on Meta and goes
 * back to review. Nothing while Meta reviews it, or once Meta disabled it.
 */
export function templateEditMode(template: {
  status: TemplateStatus;
  providerTemplateId: string | null;
}): "draft" | "meta" | null {
  if (!template.providerTemplateId) return template.status === "pending" ? null : "draft";
  return ["approved", "rejected", "paused"].includes(template.status) ? "meta" : null;
}

/** "Elimina": anything but a draft on its way to Meta, which would arrive anyway. */
export function templateDeletable(template: {
  status: TemplateStatus;
  providerTemplateId: string | null;
}): boolean {
  return Boolean(template.providerTemplateId) || template.status !== "pending";
}

export const connectorInputSchema = z
  .object({
    kind: connectorKindSchema,
    name: z.string().trim().min(1).max(160),
    baseUrl: z.string().url().max(2_048),
    apiKey: z.string().min(16).max(4_096),
    capabilities: z.array(connectorCapabilitySchema).min(1),
  })
  .strict();
export type ConnectorInput = z.infer<typeof connectorInputSchema>;

/**
 * What "Prepara con AI" should prepare: everything useful, or one kind of action.
 * `new_site`: on the operator's request only, the draft of another site for another
 * business of the same customer; the sites the lead has stay as they are (owner, 2026-10-24).
 */
export const AI_FOCUSES = [
  "all",
  "message",
  "lead_update",
  "site",
  "appointment",
  "new_site",
] as const;
export const aiFocusSchema = z.enum(AI_FOCUSES);
export type AiFocus = z.infer<typeof aiFocusSchema>;

/**
 * The OpenAI models an operator can ask a proposal from (ADR 0077): Luna, the
 * default and about 20 times cheaper, or Sol.
 */
export const AI_MODEL_CHOICES = ["luna", "sol"] as const;
export const aiModelChoiceSchema = z.enum(AI_MODEL_CHOICES);
export type AiModelChoice = z.infer<typeof aiModelChoiceSchema>;

export const AI_MODEL_IDS: Readonly<Record<AiModelChoice, string>> = {
  luna: "gpt-6-luna",
  sol: "gpt-6.1-sol",
};

export const AI_MODEL_LABELS: Readonly<Record<AiModelChoice, string>> = {
  luna: "Luna",
  sol: "Sol",
};

/** Luna or Sol for a model as OpenAI names it, dated snapshot included; null for any other. */
export function aiModelChoiceOf(model: string | null | undefined): AiModelChoice | null {
  const name = model
    ?.trim()
    .toLowerCase()
    .replace(/-\d{4}-\d{2}-\d{2}$/u, "");
  return AI_MODEL_CHOICES.find((choice) => AI_MODEL_IDS[choice] === name) ?? null;
}

export const aiProposalRequestSchema = z
  .object({
    triggerMessageId: uuidSchema.nullable().optional(),
    focus: aiFocusSchema.default("all"),
    /** Free-text request of the operator, e.g. "proponi la chiamata alle 16:30". */
    instructions: z.string().trim().max(2_000).optional(),
    /** Omitted: the workspace's default model. */
    model: aiModelChoiceSchema.optional(),
  })
  .strict();
export type AiProposalRequest = z.infer<typeof aiProposalRequestSchema>;

/** "Rigenera con Sol" on a proposal (ADR 0077); no body: the default model. */
export const proposalRegenerateRequestSchema = z
  .object({ model: aiModelChoiceSchema.optional() })
  .strict();
export type ProposalRegenerateRequest = z.infer<typeof proposalRegenerateRequestSchema>;

/** The workspace's instructions for the AI: the whole offer and the house rules (ADR 0051). */
export const AI_INSTRUCTIONS_MAX_LENGTH = 20_000;

export const workspaceConfigInputSchema = z
  .object({
    aiEnabled: z.boolean(),
    sendsEnabled: z.boolean(),
    webhookEnabled: z.boolean(),
    aiTone: z.string().trim().max(2_000),
    /** Offer, prices, signature and limits the AI must follow; kept when omitted. */
    aiInstructions: z.string().trim().max(AI_INSTRUCTIONS_MAX_LENGTH).optional(),
    /** The knowledge moved to the wiki (ADR 0075): ignored, older pages may still send it. */
    aiKnowledge: z.string().optional(),
    /** Examples from the messages we sent in similar situations (ADR 0073); kept when omitted. */
    aiExamples: z.boolean().optional(),
    aiFieldAllowlist: z
      .array(z.string().trim().min(1).max(80))
      .max(50)
      .transform((fields) => [...new Set(fields)]),
    /** The AI may search the lead's public Instagram and Facebook pages (ADR 0034); kept when omitted. */
    aiSocialLookup: z.boolean().optional(),
    /** The AI may search the web for the lead's business (ADR 0064); kept when omitted. */
    aiWebSearch: z.boolean().optional(),
    /** Opening a chat in Polaris sends the contact the read receipts (ADR 0048); kept when omitted. */
    whatsappReadReceipts: z.boolean().optional(),
    retentionDays: z.number().int().min(30).max(3_650),
    timezone: z.string().trim().min(1).max(100),
    /** Follow-up cadence; kept when omitted. */
    followupCadence: followupCadenceSchema.optional(),
    /** The morning pass prepares the follow-ups of leads who wrote with the AI (ADR 0055); kept when omitted. */
    followupAi: z.boolean().optional(),
    /** The AI reads a new contact's first message and puts spam aside (ADR 0093); kept when omitted. */
    spamFilter: z.boolean().optional(),
  })
  .strict();
export type WorkspaceConfigInput = z.infer<typeof workspaceConfigInputSchema>;

/** "Modifica con AI" of the instructions or of a wiki page (ADR 0073, 0075): nothing is saved. */
export const AI_TEXT_EDIT_MAX_LENGTH = 60_000;
export const aiTextEditRequestSchema = z
  .object({
    field: z.enum(["instructions", "wiki_text", "wiki_qa"]),
    text: z.string().max(AI_TEXT_EDIT_MAX_LENGTH),
    request: z.string().trim().min(1).max(4_000),
  })
  .strict();
export type AiTextEditRequest = z.infer<typeof aiTextEditRequestSchema>;

/** One change the AI proposes: `find` is text that appears exactly once ("" = append at the end). */
export const aiTextEditSchema = z
  .object({
    find: z.string().max(AI_TEXT_EDIT_MAX_LENGTH),
    replace: z.string().max(AI_TEXT_EDIT_MAX_LENGTH),
  })
  .strict();
export type AiTextEdit = z.infer<typeof aiTextEditSchema>;

export type AiTextEditResult = {
  /** What changed, in a few words, for the operator. */
  summary: string;
  edits: AiTextEdit[];
  /** The text with the edits applied: shown before saving, never saved by the API. */
  text: string;
};

/** An appointment written by hand from the chat or the calendar (ADR 0059). */
export const appointmentInputSchema = z
  .object({
    /** null = a commitment without lead (meeting, holiday): it only counts for overlaps. */
    leadId: uuidSchema.nullable(),
    memberId: uuidSchema,
    title: z.string().trim().min(1).max(200),
    mode: appointmentModeSchema,
    location: z.string().trim().max(500).nullable(),
    note: z.string().trim().max(2_000).nullable(),
    startsAt: z.string().datetime({ offset: true }),
    durationMinutes: appointmentDurationSchema,
    reminderMinutes: appointmentReminderSchema,
  })
  .strict();
export type AppointmentInput = z.infer<typeof appointmentInputSchema>;

/** Edit, move, hand over to a colleague or close with an outcome; `status: "scheduled"` reopens. */
export const appointmentUpdateInputSchema = z
  .object({
    version: z.number().int().positive(),
    memberId: uuidSchema.optional(),
    title: z.string().trim().min(1).max(200).optional(),
    mode: appointmentModeSchema.optional(),
    location: z.string().trim().max(500).nullable().optional(),
    note: z.string().trim().max(2_000).nullable().optional(),
    startsAt: z.string().datetime({ offset: true }).optional(),
    durationMinutes: appointmentDurationSchema.optional(),
    reminderMinutes: appointmentReminderSchema.optional(),
    status: appointmentStatusSchema.optional(),
  })
  .strict();
export type AppointmentUpdateInput = z.infer<typeof appointmentUpdateInputSchema>;

export function canManageWorkspace(role: WorkspaceRole): boolean {
  return role === "owner" || role === "admin";
}

export function canOperate(role: WorkspaceRole): boolean {
  return role !== "reviewer";
}

/**
 * Meta identifies WhatsApp contacts with the international number without a
 * leading plus. Keep that representation at provider boundaries while lead
 * records remain human-readable E.164 values.
 */
export function normalizeWhatsappContactId(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/gu, "");
  const internationalDigits = trimmed.startsWith("00") ? digits.slice(2) : digits;
  if (!/^[1-9]\d{6,14}$/.test(internationalDigits)) return null;
  return internationalDigits;
}

export function whatsappContactIdToE164(contactId: string): string | null {
  const normalized = normalizeWhatsappContactId(contactId);
  return normalized ? `+${normalized}` : null;
}

/**
 * The phone of a lead form as E.164. Meta pre-fills the country code of the
 * person's account, so an Italian mobile typed into a "+1" field arrives as
 * "+1 3xx…", and numbers may also arrive without a country code: in a workspace
 * on Italian time both become +39. Other numbers keep their country.
 */
export function leadFormPhone(raw: string, timeZone: string): string | null {
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/gu, "");
  if (timeZone === "Europe/Rome") {
    if (trimmed.startsWith("+") || trimmed.startsWith("00")) {
      const international = trimmed.startsWith("00") ? digits.slice(2) : digits;
      if (/^13\d{8,9}$/.test(international)) return `+39${international.slice(1)}`;
    } else if (/^(3\d{8,9}|0\d{5,10})$/.test(digits)) {
      return `+39${digits}`;
    }
  }
  return whatsappContactIdToE164(trimmed);
}

export function leadContactIdentityMatches(
  first: { phone?: string | null; email?: string | null },
  second: { phone?: string | null; email?: string | null },
): boolean {
  const firstPhone = normalizeWhatsappContactId(first.phone);
  const secondPhone = normalizeWhatsappContactId(second.phone);
  if (firstPhone && secondPhone && firstPhone === secondPhone) return true;

  const firstEmail = first.email?.trim().toLowerCase() || null;
  const secondEmail = second.email?.trim().toLowerCase() || null;
  return Boolean(firstEmail && secondEmail && firstEmail === secondEmail);
}

export function isFreeformWindowOpen(lastInboundAt: string | null, now = new Date()): boolean {
  if (!lastInboundAt) return false;
  const receivedAt = new Date(lastInboundAt);
  return (
    Number.isFinite(receivedAt.getTime()) &&
    now.getTime() - receivedAt.getTime() < 24 * 60 * 60 * 1_000
  );
}

const messageStatusRank: Record<MessageStatus, number> = {
  queued: 0,
  sent: 1,
  delivered: 2,
  read: 3,
  failed: 4,
  received: 4,
};

export function canAdvanceMessageStatus(current: MessageStatus, next: MessageStatus): boolean {
  if (current === next) return true;
  if (current === "failed" || current === "received") return false;
  if (next === "failed") return true;
  return messageStatusRank[next] > messageStatusRank[current];
}
